import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Users, Gift, TrendingUp, Crown, Copy, Share2, MousePointer, Target, Activity } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { generateShareableReferralUrl, fetchTenantDataForCustomer } from '@/utils/tenantReferralUtils'

interface ReferralData {
  id: string
  email: string
  full_name: string
  created_at: string
  visit_stamps: number
  referral_stamps: number
}

interface ReferralStats {
  totalReferrals: number
  chainDepth: number
  chainCount: number
  recentReferrals: ReferralData[]
  customerData: {
    id: string
    referral_code: string
    referral_stamps: number
    total_referrals: number
  }
  tenantData?: any
  referralUrl?: string
  linkClicks: number
  directClicks: number
  crossDomainClicks: number
  conversions: number
  conversionRate: number
  todayClicks: number
  todayConversions: number
}

const ReferralStats = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [stats, setStats] = useState<ReferralStats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchReferralStats()
  }, [user])

  const fetchReferralStats = async () => {
    if (!user?.email) return

    try {
      // Get current customer data
      const { data: customer, error: customerError } = await supabase
        .from('customers')
        .select('id, referral_code, referral_stamps, total_referrals')
        .eq('email', user.email)
        .single()

      if (customerError) throw customerError

      // Get tenant data for referral URL
      const tenantData = await fetchTenantDataForCustomer(customer.id)
      const referralUrl = generateShareableReferralUrl(customer.referral_code, tenantData)

      // Get direct referrals
      const { data: referrals, error: referralsError } = await supabase
        .from('customers')
        .select('id, email, full_name, created_at, visit_stamps, referral_stamps')
        .eq('referred_by', customer.id)
        .order('created_at', { ascending: false })

      if (referralsError) throw referralsError

      // Get referral chain statistics using the database functions
      const { data: chainDepthResult } = await supabase
        .rpc('get_referral_chain_depth', { customer_uuid: customer.id })

      const { data: chainCountResult } = await supabase
        .rpc('count_referral_chain', { customer_uuid: customer.id })

      // Get link click statistics from audit logs with enhanced tracking
      const { data: linkClicksData } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('action', 'referral_link_accessed')
        .contains('details', { referral_code: customer.referral_code })

      const { data: conversionsData } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('action', 'referral_processed')
        .contains('details', { referral_code: customer.referral_code })

      // Process clicks by tracking type  
      const totalClicks = linkClicksData?.length || 0
      const crossDomainClicks = linkClicksData?.filter(log => {
        const details = log.details as any
        return details?.tracking_type === 'cross_domain'
      }).length || 0
      const directClicks = totalClicks - crossDomainClicks

      // Calculate today's stats
      const today = new Date().toISOString().split('T')[0]
      const todayClicks = linkClicksData?.filter(log => 
        log.created_at.startsWith(today)
      ).length || 0

      const todayConversions = conversionsData?.filter(log => 
        log.created_at.startsWith(today)
      ).length || 0

      const totalConversions = conversionsData?.length || 0
      const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0

      setStats({
        totalReferrals: referrals.length,
        chainDepth: chainDepthResult || 0,
        chainCount: chainCountResult || 0,
        recentReferrals: referrals.slice(0, 10), // Show last 10 referrals
        customerData: customer,
        tenantData,
        referralUrl,
        linkClicks: totalClicks,
        directClicks,
        crossDomainClicks,
        conversions: totalConversions,
        conversionRate: Math.round(conversionRate * 100) / 100,
        todayClicks,
        todayConversions
      })

    } catch (error) {
      console.error('Error fetching referral stats:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Empfehlungsstatistiken konnten nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const copyReferralLink = async () => {
    if (!stats?.referralUrl) return
    
    try {
      await navigator.clipboard.writeText(stats.referralUrl)
      toast({
        title: "Link kopiert!",
        description: "Der Empfehlungslink wurde in die Zwischenablage kopiert.",
      })
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Der Link konnte nicht kopiert werden.",
      })
    }
  }

  const shareReferralLink = async () => {
    if (!stats?.referralUrl) return
    
    const tenantName = stats.tenantData?.name || 'diesem Unternehmen'
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${tenantName} - Digitale Stempelkarten`,
          text: `Hol dir auch eine digitale Stempelkarte bei ${tenantName} und sammle Punkte!`,
          url: stats.referralUrl,
        })
      } catch (error) {
        // User cancelled or error occurred, fallback to copy
        copyReferralLink()
      }
    } else {
      copyReferralLink()
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center">
            <p>Wird geladen...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!stats) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <CardTitle>Empfehlungsstatistiken nicht verfügbar</CardTitle>
              <CardDescription>
                Ihre Empfehlungsstatistiken konnten nicht geladen werden.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Empfehlungsstatistiken</h1>
          <p className="text-muted-foreground">
            Verfolgen Sie Ihre Empfehlungen und verdiente Stempel im Detail.
          </p>
        </div>

        {/* Stats Overview with enhanced cross-domain tracking */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <MousePointer className="h-6 w-6 text-blue-500" />
                <p className="text-2xl font-bold">{stats.linkClicks}</p>
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Link-Klicks</p>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Direkt: {stats.directClicks}</span>
                  <span>Cross-Domain: {stats.crossDomainClicks}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Users className="h-8 w-8 text-green-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">{stats.conversions}</p>
                <p className="text-sm text-muted-foreground">Geworbene Kunden</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Activity className="h-8 w-8 text-orange-500 mr-3" />
              <div>
                <p className="text-2xl font-bold">{stats.conversionRate}%</p>
                <p className="text-sm text-muted-foreground">Konversionsrate</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="flex items-center p-6">
              <Gift className="h-8 w-8 text-accent mr-3" />
              <div>
                <p className="text-2xl font-bold">{stats.customerData.referral_stamps}</p>
                <p className="text-sm text-muted-foreground">Empfehlungsstempel</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <TrendingUp className="h-8 w-8 text-success mr-3" />
              <div>
                <p className="text-2xl font-bold">{stats.chainDepth}</p>
                <p className="text-sm text-muted-foreground">Empfehlungstiefe</p>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="flex items-center p-6">
              <Crown className="h-8 w-8 text-warning mr-3" />
              <div>
                <p className="text-2xl font-bold">{stats.chainCount}</p>
                <p className="text-sm text-muted-foreground">Gesamt-Netzwerk</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Today's Performance with clear separation */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Heute's Performance</CardTitle>
            <CardDescription>
              Live-Tracking Ihrer heutigen Empfehlungsaktivitäten
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-2">
                  <MousePointer className="h-5 w-5 text-blue-500" />
                  <span className="font-medium text-blue-700 dark:text-blue-300">Heute's Klicks</span>
                </div>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.todayClicks}</p>
                <p className="text-xs text-blue-600 dark:text-blue-400">Link-Aufrufe</p>
              </div>
              
              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-300">Heute's Kunden</span>
                </div>
                <p className="text-2xl font-bold text-green-900 dark:text-green-100">{stats.todayConversions}</p>
                <p className="text-xs text-green-600 dark:text-green-400">Geworbene Kunden</p>
              </div>
              
              <div className="p-4 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <div className="flex items-center gap-2 mb-2">
                  <Gift className="h-5 w-5 text-orange-500" />
                  <span className="font-medium text-orange-700 dark:text-orange-300">Verdiente Stempel</span>
                </div>
                <p className="text-2xl font-bold text-orange-900 dark:text-orange-100">{stats.customerData.referral_stamps}</p>
                <p className="text-xs text-orange-600 dark:text-orange-400">Empfehlungsstempel</p>
              </div>

              <div className="p-4 bg-purple-50 dark:bg-purple-950/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <div className="flex items-center gap-2 mb-2">
                  <Activity className="h-5 w-5 text-purple-500" />
                  <span className="font-medium text-purple-700 dark:text-purple-300">Conversion Rate</span>
                </div>
                <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  {stats.todayClicks > 0 ? Math.round((stats.todayConversions / stats.todayClicks) * 100) : 0}%
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">Heute's Erfolgsrate</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referral Link Card */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Ihr Empfehlungslink</CardTitle>
            <CardDescription>
              Teilen Sie diesen Link und erhalten Sie Stempel für jeden neuen Kunden. Links werden automatisch über alle Domains getrackt.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-4">
              <code className="flex-1 p-3 bg-muted rounded text-sm break-all">
                {stats.referralUrl}
              </code>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={copyReferralLink} variant="outline" className="flex-1">
                <Copy className="h-4 w-4 mr-2" />
                Link kopieren
              </Button>
              <Button onClick={shareReferralLink} className="flex-1">
                <Share2 className="h-4 w-4 mr-2" />
                Teilen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Referrals */}
        <Card>
          <CardHeader>
            <CardTitle>Ihre Empfehlungen</CardTitle>
            <CardDescription>
              Kürzlich über Ihren Link registrierte Kunden
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {stats.recentReferrals.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Noch keine Empfehlungen</h3>
                <p className="text-muted-foreground">
                  Teilen Sie Ihren Empfehlungslink und sammeln Sie Stempel für jeden neuen Kunden!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {stats.recentReferrals.map((referral) => (
                  <div key={referral.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                      
                      <div>
                        <p className="font-medium">
                          {referral.full_name || referral.email}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Registriert am {format(new Date(referral.created_at), 'dd.MM.yyyy', { locale: de })}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">
                        {referral.visit_stamps} Besuche
                      </Badge>
                      <Badge variant="secondary">
                        {referral.referral_stamps} Empfehlungen
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}

export default ReferralStats